import styled from '@emotion/styled'
import { calcRem, colors, breakpoint, fonts } from '../__styling'
import { themedValue } from '../__styling/themed-value'
import { createPropFilter } from '@news-mono/web-common'
import { StyledVideoPlayerDescriptionMoreLess } from '../VideoHub/VideoPlayer/VideoPlayer.styled'

export const StyledOverlay = styled('div')({
    position: 'fixed',
    inset: 0,
    width: '100%',
    height: 'calc(var(--vertical-vh, 1vh) * 100)',
    background: 'rgba(0,0,0,0.92)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 9999,
})

export const StyledFeedScroll = styled('div')({
    position: 'absolute',
    inset: 0,
    overflowY: 'scroll',
    scrollSnapType: 'y mandatory',
    overscrollBehavior: 'none',
    WebkitOverscrollBehavior: 'none',
    zIndex: 1,
})

export const StyledScreen = styled('div')({
    width: '100%',
    height: 'calc(var(--vertical-vh, 1vh) * 100)',
    scrollSnapAlign: 'start',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
})

export const StyledConsentContainer = styled('div')(({ theme }) => ({
    width: 'calc(var(--vertical-vh, 1vh) * 95 * 9 / 16)',
    height: 'calc(var(--vertical-vh, 1vh) * 95)',
    maxWidth: '90vw',
    borderRadius: calcRem(12),
    overflow: 'hidden',
    background: themedValue(theme, {
        fallback: colors.white,
    }),
    boxShadow: '0 6px 24px rgba(0, 0, 0, 0.35)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}))

export const StyledPlayerWrapper = styled('div')({
    position: 'relative',
    width: '100vw',
    height: 'calc(var(--vertical-vh, 1vh) * 100)',
    background: colors.black,
    overflow: 'hidden',

    [breakpoint('xs')]: {
        height: 'calc(var(--vertical-vh, 1vh) * 95)',
        width: 'calc(var(--vertical-vh, 1vh) * 95 * 9 / 16)',
        borderRadius: calcRem(12),
    },

    '& .article-video, & .video-specificity, & .video-wrapper, & [class*="StyledArticleVideoContainer"], & [class*="StyledWrapper"] ':
        {
            width: '100%',
            height: '100%',
        },

    '& [class*="StyledArticleVideoContainer"]': {
        margin: 0,
        padding: 0,
        paddingBottom: 0,
        position: 'relative',
    },

    '& .vjs-errors-content-container': {
        top: calcRem(55),
        left: calcRem(20),
        right: calcRem(20),
        bottom: calcRem(20),
        border: '1px #999 solid',
        height: `calc(100% - ${calcRem(75)})`,
        width: `calc(100% - ${calcRem(40)})`,
        paddingLeft: '3%',
        paddingRight: '3%',
    },

    '& [class*="StyledVideoErrorBoundary"]': {
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: calcRem(8),
        width: '100%',
        height: '100%',
        color: colors.white,
    },

    '& .video-specificity .css-honeqx-StyledBrightcoveVideo .vjs-control.vjs-button':
        {
            right: calcRem(32),
            top: calcRem(55),
        },
})

export const StyledAdPlaceholder = styled('div')({
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: colors.white,
    fontFamily: fonts.sevennews.sansSerif,
    fontSize: calcRem(16),
    letterSpacing: '0.02em',
    position: 'relative',

    '& > div, & > video': {
        width: '100%',
        height: '100%',
    },
})

interface StyledButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    className?: string
}

export const StyledButtonBase = styled('button', {
    shouldForwardProp: createPropFilter<StyledButtonProps>()(['className']),
})<StyledButtonProps>(({ theme }) => ({
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 20,
    width: calcRem(34),
    height: calcRem(34),
    borderRadius: '50%',
    border: 'none',
    cursor: 'pointer',
    background: colors.white,
    color: colors.black,
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
    fontSize: calcRem(18),
    fontWeight: 600,
    transition: 'background 0.2s ease, color 0.2s ease',
    right: calcRem(12),
    left: 'auto',
    boxShadow: '0 4px 12px rgba(0,0,0,0.35)',

    '&:disabled': {
        opacity: 0.3,
        cursor: 'default',
    },

    '&:not(:disabled):hover': {
        background: themedValue(theme, {
            sevennews: (section) => section.video.focusColor,
            fallback: colors.white,
        }),
        color: themedValue(theme, {
            sevennews: (section) => colors.white,
            fallback: colors.black,
        }),
    },
    [breakpoint('md')]: {
        width: calcRem(44),
        height: calcRem(44),
        right: 'auto',
    },
}))

export const StyledArrowUp = styled(StyledButtonBase)({
    marginTop: calcRem(-35),
    [breakpoint('md')]: {
        left: 'var(--arrow-offset)',
    },
})

export const StyledArrowDown = styled(StyledButtonBase)({
    marginTop: calcRem(35),
    '& svg': {
        height: calcRem(24),
        width: calcRem(24),
        rotate: '180deg',
    },
    [breakpoint('md')]: {
        left: 'var(--arrow-offset)',
    },
})

export const StyledTopBar = styled('div')({
    position: 'absolute',
    top: calcRem(16),
    zIndex: 30,

    left: calcRem(20),
    right: 'auto',

    [breakpoint('xs')]: {
        left: 'auto',
        right: 0,
        top: 0,
    },
})

export const StyledCloseButton = styled('button')(({ theme }) => ({
    width: calcRem(24),
    height: calcRem(24),
    borderRadius: '50%',
    background: 'transparent',
    color: '#fff',
    border: 'none',
    cursor: 'pointer',
    fontSize: calcRem(24),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    transition: 'background 0.2s ease, color 0.2s ease',

    left: calcRem(20),
    right: 'auto',

    [breakpoint('xs')]: {
        background: 'rgba(0,0,0,0.7)',
        left: 'auto',
        marginRight: calcRem(20),
        marginTop: calcRem(20),
        width: calcRem(54),
        height: calcRem(54),
    },

    '&:hover': {
        background: themedValue(theme, {
            sevennews: (section) => section.video.focusColor,
            fallback: 'rgba(0,0,0,0.85)',
        }),
        color: themedValue(theme, {
            sevennews: (section) => colors.white,
            fallback: colors.black,
        }),
    },
}))

export const StyledVideoInfoOverlay = styled('div')({
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 15,
    padding: calcRem(16),
    color: colors.white,
    textShadow: '0 1px 2px rgba(0,0,0,0.6)',
    background:
        'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.55) 30%, rgba(0,0,0,0.9) 100%)',
    pointerEvents: 'auto',
    [breakpoint('sm')]: {
        padding: calcRem(20),
    },
})

export const StyledVideoInfoTitle = styled('h2')(({ theme }) => ({
    margin: 0,
    marginBottom: calcRem(6),
    fontFamily: themedValue(theme, {
        thewest: fonts.thewest.serif,
        perthnow: fonts.perthnow.sansSerif,
        fallback: fonts.sevennews.sansSerif,
    }),
    color: colors.white,
    fontSize: calcRem(19),
    lineHeight: themedValue(theme, {
        thewest: calcRem(28),
        fallback: calcRem(23),
    }),
    fontWeight: themedValue(theme, {
        thewest: 400,
        fallback: 600,
    }),
    display: 'block',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    [breakpoint('sm')]: {
        fontSize: calcRem(21),
        lineHeight: calcRem(25),
    },
}))

export const StyledVideoInfoDescription = styled('div')(({ theme }) => ({
    fontSize: calcRem(16),
    lineHeight: 1.5,
    [`${StyledVideoPlayerDescriptionMoreLess}`]: {
        marginBottom: 0,
        minHeight: 'auto',
    },
    '& button': {
        color: colors.white,
        fontSize: themedValue(theme, {
            thewest: calcRem(12),
            perthnow: calcRem(18),
            thenightly: calcRem(18),
            fallback: undefined,
        }),
        opacity: themedValue(theme, {
            sevennews: 0.7,
            fallback: 1,
        }),
    },
    '& a': {
        color: theme.sectionValues?.primaryColor,
    },
    [breakpoint('sm')]: {
        fontSize: calcRem(16),
    },
}))
